import {
  faChevronDown,
  faChevronUp,
  faFile,
  faList,
  faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Feature from 'src/components/feature';
import useDocuments from 'src/hooks/services/useDocuments';
import { FEATURES } from 'src/utils/features';

import RunDocuments from './_run-documents';

const FieldItem = ({ label, value }) => (
  <div className="shared-custom-fields-card__item">
    <div className="shared-custom-fields-card__item-name">{label}:</div>
    <div className="shared-custom-fields-card__item-value">{value}</div>
  </div>
);

FieldItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
};

const SharedCustomFields = ({ buildData, estMaterialWeight, estMaterialWeightUnits, additionalDocuments }) => {
  const { documentList, userList } = useDocuments(additionalDocuments);
  const { customFieldEntries: customFields, additive } = buildData || {};

  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpanded = () => setIsExpanded((prev) => !prev);

  const hasCustomFields = customFields?.length > 0;

  return (
    <div className='shared-custom-fields-card card-base'>
      <div
        className={`shared-custom-fields-card__header ${isExpanded ? 'shared-custom-fields-card__header--expanded' : ''}`}
        onClick={toggleExpanded}
      >
        <div className="shared-custom-fields-card__left card-left">
          <div className={`shared-custom-fields-card__icon icon-container ${isExpanded ? 'shared-custom-fields-card__icon--expanded' : ''}`}>
            <FontAwesomeIcon icon={faList} />
          </div>
          <div className="shared-custom-fields-card__info info-section">
            <div className={`shared-custom-fields-card__title ${isExpanded ? 'shared-custom-fields-card__title--expanded' : ''}`}>
              BUILD DETAILS
            </div>
            <div className="shared-custom-fields-card__badges">
              <span className="shared-custom-fields-card__badge shared-custom-fields-card__badge--info">
                <FontAwesomeIcon icon={faFile} className="shared-custom-fields-card__badge-icon" />
                {additionalDocuments ? additionalDocuments.length : 0}
              </span>
            </div>
            {!isExpanded && (
              <div className="shared-custom-fields-card__subtitle subtitle-text">Tap to view details</div>
            )}
          </div>
        </div>
        <div className="shared-custom-fields-card__right">
          <FontAwesomeIcon
            icon={isExpanded ? faChevronUp : faChevronDown}
            className="shared-custom-fields-card__chevron"
          />
        </div>
      </div>
      <div className="expandable-card-body-wrapper" data-open={isExpanded}>
        <div className="expandable-card-body-inner">
          <div className="shared-custom-fields-card__body">
            <div className="shared-custom-fields-card__section">
              <div className="shared-custom-fields-card__section-title">Primary Run Settings</div>
              <FieldItem label="Layer Thickness" value={additive?.layer_thickness || '-'} />
              <FieldItem
                label="Estimated Build Weight"
                value={
                estMaterialWeight && estMaterialWeightUnits
                  ? `${estMaterialWeight} ${estMaterialWeightUnits}`
                  : '-'
              }
            />
            </div>

            <Feature featureName={FEATURES.CUSTOM_FIELDS_IN_RUNS}>
              <div className="shared-custom-fields-card__section">
                <div className="shared-custom-fields-card__section-title">Additional Custom Settings</div>
                {hasCustomFields ? (
                  <div className="shared-custom-fields-card__list">
                    {customFields.map((field) => (
                      <FieldItem
                        key={`${field.name}-${field.value || 'empty'}`}
                        label={field.name}
                        value={field.value || '-'}
                    />
                  ))}
                  </div>
              ) : (
                <div className="shared-custom-fields-card__empty-message">
                  There are no custom settings that apply
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="custom-settings-tooltip">
                        To add additional Custom settings, please create custom fields for your Line Items, Runs, or Orders
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="shared-custom-fields-card__tooltip-icon" />
                  </OverlayTrigger>
                </div>
              )}
              </div>
            </Feature>

            <div className="shared-custom-fields-card__section">
              <div className="shared-custom-fields-card__section-title">Additional Documents</div>
              <div className="shared-custom-fields-card__documents">
                <RunDocuments documents={documentList} users={userList} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

SharedCustomFields.propTypes = {
  buildData: PropTypes.shape({
    customFields: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string,
      })
    ),
    additive: PropTypes.shape({
      // eslint-disable-next-line camelcase
      layer_thickness: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  additionalDocuments: PropTypes.arrayOf(PropTypes.string),
  estMaterialWeight: PropTypes.number.isRequired,
  estMaterialWeightUnits: PropTypes.string.isRequired,
};

SharedCustomFields.defaultProps = {
  buildData: {
    customFields: [],
    additive: {},
    estimatesAdditive: {},
  },
};

export default SharedCustomFields;
